import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fileExtension,
  formatFileSize,
  pruneSelected,
  toggleAll,
  toggleSelected,
} from '@/lib/fileTable';

export interface FileTableRow {
  id: string;
  filename: string;
  byteCount: number;
  /** Uploader email — external customer or internal practitioner; null
   *  for rows that predate uploader tracking. */
  uploadedByEmail: string | null;
  /** Attachment count (invoice links); shown as a column when any row
   *  carries it, and called out in the delete confirmation. */
  linkCount?: number;
}

/** Compact, reusable file table on the design-system Table shell:
 *  select column for bulk actions, filename, extension badge, size,
 *  uploader email, per-row Remove. Every delete — single or bulk —
 *  goes through an are-you-sure dialog. The parent owns the actual
 *  deletion (API calls + refetch) via `onDelete`. */
export function FileTable({
  rows,
  onDelete,
  deleting = false,
}: {
  rows: FileTableRow[];
  /** Delete the given ids; resolve when done (parent refetches). */
  onDelete: (ids: string[]) => Promise<void>;
  deleting?: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmIds, setConfirmIds] = useState<string[] | null>(null);

  const visibleIds = rows.map((r) => r.id);
  const effectiveSelected = pruneSelected(selected, visibleIds);
  const allSelected = visibleIds.length > 0 && effectiveSelected.size === visibleIds.length;
  const showLinks = rows.some((r) => r.linkCount !== undefined);

  const confirmRows = rows.filter((r) => confirmIds?.includes(r.id));
  const confirmLinkCount = confirmRows.reduce((n, r) => n + (r.linkCount ?? 0), 0);

  const runDelete = async () => {
    if (!confirmIds) return;
    await onDelete(confirmIds);
    setSelected(new Set());
    setConfirmIds(null);
  };

  return (
    <div className="grid gap-2">
      {effectiveSelected.size > 0 && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-brand-slate">
            {effectiveSelected.size} selected
          </span>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => setConfirmIds([...effectiveSelected])}
            disabled={deleting}
          >
            Delete selected ({effectiveSelected.size})
          </Button>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">
              <input
                type="checkbox"
                aria-label="Select all files"
                checked={allSelected}
                onChange={() => setSelected(toggleAll(effectiveSelected, visibleIds))}
              />
            </TableHead>
            <TableHead>File name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Size</TableHead>
            <TableHead>Uploaded by</TableHead>
            {showLinks && <TableHead className="text-right">Links</TableHead>}
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <input
                  type="checkbox"
                  aria-label={`Select ${row.filename}`}
                  checked={effectiveSelected.has(row.id)}
                  onChange={() => setSelected(toggleSelected(effectiveSelected, row.id))}
                />
              </TableCell>
              <TableCell className="max-w-[28rem] truncate font-medium" title={row.filename}>
                {row.filename}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="font-mono text-[10px]">
                  {fileExtension(row.filename)}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {formatFileSize(row.byteCount)}
              </TableCell>
              <TableCell className="text-brand-slate">{row.uploadedByEmail ?? '—'}</TableCell>
              {showLinks && (
                <TableCell className="text-right font-mono tabular-nums">
                  {row.linkCount ?? 0}
                </TableCell>
              )}
              <TableCell className="text-right">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmIds([row.id])}
                  disabled={deleting}
                >
                  Remove
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={confirmIds !== null} onClose={() => setConfirmIds(null)}>
        <DialogHeader>
          <DialogTitle>
            Delete {confirmRows.length === 1 ? confirmRows[0]?.filename : `${confirmRows.length} files`}?
          </DialogTitle>
          <DialogDescription>
            This permanently removes {confirmRows.length === 1 ? 'the file' : 'these files'} from
            storage.
            {confirmLinkCount > 0 && (
              <>
                {' '}
                {confirmLinkCount} transaction attachment{confirmLinkCount === 1 ? '' : 's'} will
                also be removed.
              </>
            )}{' '}
            This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setConfirmIds(null)}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={runDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
